"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

type Mode = "encode" | "decode";

const ALPHABET_SIZE = 26;
const UPPER_A = 65;
const LOWER_A = 97;
const MAX_WORKING_ROWS = 20;

/*
 * This one stays ASCII, and the widget now says so instead of pretending.
 *
 * `isLetter` and `shiftChar` only move `A-Z`/`a-z`, so every other letter fell through
 * the `output += char` branch untouched: a reader who pasted "Атака на рассвете",
 * "هجوم عند الفجر", "भोर में हमला", "새벽에 공격" or "Tấn công lúc bình minh" got their
 * own plaintext back, labelled "Cipher text", with nothing anywhere saying the tool had
 * declined to encipher it. Vietnamese and German are the dangerous middle: "Tấn công lúc
 * bình minh" leaves 4 of its 18 letters in clear and "Grüße aus Köln" 3 of 12 — output
 * that looks encrypted and partly is not.
 *
 * Widening the shift was considered and rejected. A Vigenère cipher is defined over an
 * alphabet, and there is no canonical tableau to widen it to: Russian would need 33
 * letters, Greek 24, Arabic 28 with positional forms, and Hindi, Korean, Chinese and
 * Japanese have no alphabet of the required kind at all. Any size we picked would be an
 * invention, so a message enciphered here would decode only here — and this is a tool
 * for a classroom exercise about the historical cipher, where the 26-letter tableau is
 * the thing being taught. Naming the limit is the honest fix; silently returning the
 * plaintext is not. Same call the site already made for `letter-frequency-counter`.
 */
const KEYWORD_REGEX = /^[a-zA-Z]*$/;
const RE_ANY_LETTER = /\p{L}/u;

interface WorkingRow {
  plain: string;
  keyLetter: string;
  shift: number;
  result: string;
}

function isLetter(char: string): boolean {
  const code = char.charCodeAt(0);
  if (code >= UPPER_A && code < UPPER_A + ALPHABET_SIZE) {
    return true;
  }
  return code >= LOWER_A && code < LOWER_A + ALPHABET_SIZE;
}

/** Letters the 26-letter tableau cannot take, and therefore leaves in clear. */
function countUnenciphered(text: string): number {
  return [...text].filter((char) => RE_ANY_LETTER.test(char) && !isLetter(char))
    .length;
}

function shiftChar(char: string, shift: number): string {
  const code = char.charCodeAt(0);
  if (code >= UPPER_A && code < UPPER_A + ALPHABET_SIZE) {
    return String.fromCharCode(
      UPPER_A + ((code - UPPER_A + shift) % ALPHABET_SIZE)
    );
  }
  if (code >= LOWER_A && code < LOWER_A + ALPHABET_SIZE) {
    return String.fromCharCode(
      LOWER_A + ((code - LOWER_A + shift) % ALPHABET_SIZE)
    );
  }
  return char;
}

function vigenereTransform(
  text: string,
  keyword: string,
  mode: Mode
): { output: string; rows: WorkingRow[] } {
  const key = keyword.toLowerCase();
  const rows: WorkingRow[] = [];
  let output = "";
  let keyIndex = 0;
  for (const char of text) {
    if (!isLetter(char)) {
      output += char;
      continue;
    }
    const keyLetter = key.charAt(keyIndex % key.length);
    const baseShift = keyLetter.charCodeAt(0) - LOWER_A;
    const applied =
      mode === "encode"
        ? baseShift
        : (ALPHABET_SIZE - baseShift) % ALPHABET_SIZE;
    const result = shiftChar(char, applied);
    if (rows.length < MAX_WORKING_ROWS) {
      rows.push({ plain: char, keyLetter, shift: baseShift, result });
    }
    output += result;
    keyIndex += 1;
  }
  return { output, rows };
}

/** English strings. A translated content file overrides these through `ui`. */
export const VIGENERE_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  keywordLabel: "Keyword — letters only",
  keywordPlaceholder: "LEMON",
  keywordError: "The keyword may only contain the letters A-Z.",
  unencipheredCount:
    "one: {count} letter in your text is outside A-Z and was left unchanged.|other: {count} letters in your text are outside A-Z and were left unchanged.",
  latinOnlyNote:
    "The Vigenère tableau is the 26-letter Latin alphabet, so text in any other script passes through unenciphered instead of being encrypted.",
  showWorking: "Show working — letter-by-letter shift table",
  inputPlaceholder: "Attack at dawn",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  workingHeading: "Working — first {n} letters",
  plainLetter: "Plain letter",
  cipherLetter: "Cipher letter",
  keyLetter: "Key letter",
  shift: "Shift",
  result: "Result",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
} as const;

export default function VigenereCipherWidget() {
  const strings = useToolUi(VIGENERE_CIPHER_STRINGS);
  const locale = useToolLocale();
  const [input, setInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [mode, setMode] = useState<Mode>("encode");
  const [showWorking, setShowWorking] = useState(false);
  const [copied, setCopied] = useState(false);

  const keywordValid = KEYWORD_REGEX.test(keyword);
  const unenciphered = countUnenciphered(input);
  const ready = keywordValid && keyword.length > 0 && input.length > 0;
  const { output, rows } = ready
    ? vigenereTransform(input, keyword, mode)
    : { output: "", rows: [] };

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.mode}</p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "encode"}
              className="accent-primary"
              name="cipher-mode"
              onChange={() => setMode("encode")}
              type="radio"
              value="encode"
            />
            {strings.encode}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "decode"}
              className="accent-primary"
              name="cipher-mode"
              onChange={() => setMode("decode")}
              type="radio"
              value="decode"
            />
            {strings.decode}
          </label>
        </div>
      </section>

      {/* Keyword */}
      <section className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="cipher-keyword"
        >
          {strings.keywordLabel}
        </label>
        <input
          className="w-full max-w-sm rounded-lg border border-border bg-card px-4 py-2 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id="cipher-keyword"
          onChange={(e) => {
            setKeyword(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.keywordPlaceholder}
          type="text"
          value={keyword}
        />
        {!keywordValid && (
          <p className="text-destructive text-xs" role="alert">
            {strings.keywordError}
          </p>
        )}
        <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
          <input
            checked={showWorking}
            className="accent-primary"
            onChange={(e) => setShowWorking(e.target.checked)}
            type="checkbox"
          />
          {strings.showWorking}
        </label>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="cipher-input"
        >
          {mode === "encode" ? strings.plainText : strings.cipherText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="cipher-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
        {unenciphered > 0 && (
          <output className="block text-muted-foreground text-xs leading-relaxed">
            {tPlural(strings.unencipheredCount, locale, unenciphered)}{" "}
            {strings.latinOnlyNote}
          </output>
        )}
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="cipher-output"
          >
            {mode === "encode" ? strings.cipherText : strings.decodedText}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="cipher-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Working table */}
      {showWorking && rows.length > 0 && (
        <section className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.workingHeading.replace("{n}", String(MAX_WORKING_ROWS))}
          </p>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border border-b text-muted-foreground text-xs">
                  <th className="px-4 py-2 font-medium">
                    {mode === "encode"
                      ? strings.plainLetter
                      : strings.cipherLetter}
                  </th>
                  <th className="px-4 py-2 font-medium">{strings.keyLetter}</th>
                  <th className="px-4 py-2 font-medium">{strings.shift}</th>
                  <th className="px-4 py-2 font-medium">{strings.result}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row, index) => (
                  <tr
                    className="font-mono text-foreground"
                    key={`${row.plain}-${row.keyLetter}-${index}`}
                  >
                    <td className="px-4 py-1.5">{row.plain}</td>
                    <td className="px-4 py-1.5">{row.keyLetter}</td>
                    <td className="px-4 py-1.5">
                      {mode === "encode" ? "+" : "-"}
                      {row.shift}
                    </td>
                    <td className="px-4 py-1.5">{row.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
    </>
  );
}
